import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Card components
const Card = ({ children, className = "" }) => (
  <div className={`bg-white p-4 sm:p-6 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => <div className="mb-4">{children}</div>;

const CardTitle = ({ children }) => (
  <h3 className="text-xl sm:text-2xl font-bold text-blue-600">{children}</h3>
);

const CardContent = ({ children }) => (
  <div className="text-gray-700 dark:text-gray-300 text-base sm:text-lg">
    {children}
  </div>
);

// EventCard component using the Card components
const EventCard = ({ title, children }) => (
  <Card className="h-full bg-gray-50 hover:bg-gray-100 transition-colors duration-300">
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// IconText component for displaying icons with text
const IconText = ({ Icon, text }) => (
  <div className="flex items-center space-x-2">
    <Icon className="w-6 h-6 text-blue-500" />
    <span className="text-gray-800 dark:text-gray-300">{text}</span>
  </div>
);

// Utility function to shuffle an array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const AchievementReusableComponent = ({
  title,
  subtitle,
  description,
  images,
  iconTexts,
  highlights,
  upcomingEvents,
  galleryLink,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const shuffledImages = React.useMemo(() => shuffleArray(images), [images]);

  const changeImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % shuffledImages.length);
  }, [shuffledImages.length]);

  useEffect(() => {
    const intervalId = setInterval(changeImage, 5000);
    return () => clearInterval(intervalId);
  }, [changeImage]);

  return (
    <div className="min-h-screen bg-gray-200 p-4 sm:p-6 lg:p-8 mt-4">
      <main className="space-y-8 lg:space-y-12">
        {/* Page Header */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100">
            {title}
          </h1>
          <p className="text-lg sm:text-xl mt-2 text-gray-600 dark:text-gray-400 italic">
            {subtitle}
          </p>
        </div>

        {/* Image Slider and Event Details */}
        <div className="grid gap-8 md:grid-cols-2">
          {/* Image Slider */}
          <div className="relative h-[250px] sm:h-[350px] lg:h-[400px] rounded-lg overflow-hidden shadow-lg">
            {shuffledImages.map((src, index) => (
              <img
                key={src}
                src={src}
                alt={`Event ${index + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  index === currentImageIndex ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}
          </div>

          {/* Event Details */}
          <div className="flex flex-col space-y-6">
            <div className="overflow-auto p-2 max-h-48 sm:max-h-56 lg:max-h-64">
              <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
                {description}
              </p>
            </div>

            {/* Non-Scrollable Icons Section */}
            <div className="grid grid-cols-2 gap-4">
              {iconTexts.map((item, index) => (
                <IconText key={index} Icon={item.icon} text={item.text} />
              ))}
            </div>
          </div>
        </div>

        {/* Event Highlights and Upcoming Events */}
        <div className="grid gap-8 md:grid-cols-2">
          <EventCard title="Event Highlights">
            <ul className="list-disc list-inside space-y-2">
              {highlights.map((highlight, index) => (
                <li key={index}>{highlight}</li>
              ))}
            </ul>
          </EventCard>

          {/* Upcoming Events */}
          <EventCard title="Upcoming Events">
            <div className="space-y-4">
              {upcomingEvents.map((event, index) => (
                <div key={index}>
                  <h3 className="font-semibold text-gray-900 ">
                    {event.title}
                  </h3>
                  <p className="text-sm text-gray-600 ">
                    {event.description}
                  </p>
                  <p className="text-sm text-gray-600">Date: {event.date}</p>
                </div>
              ))}
            </div>
          </EventCard>
        </div>

        {/* Event Gallery */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
            Event Gallery
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {shuffledImages.slice(0, 8).map((src, index) => (
              <div
                key={index}
                className="relative aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-md transition-all duration-300 hover:scale-105"
              >
                <img
                  src={src}
                  alt={`Event ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to={galleryLink}
              className="inline-flex items-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
            >
              View Full Gallery
            </Link>
          </div>
        </section>
      </main>
    </div>


//     <div className="min-h-screen bg-gray-200 p-4 sm:p-6 lg:p-8 mt-4">
//   <main className="space-y-8 lg:space-y-12">
//     {/* Page Header */}
//     <div className="text-center">
//       <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100">
//         {title}
//       </h1>
//       <p className="text-lg sm:text-xl mt-2 text-gray-600 dark:text-gray-400 italic">
//         {subtitle}
//       </p>
//     </div>

//     {/* Image Slider and Event Details */}
//     <div className="grid gap-8 md:grid-cols-2">
//       {/* Image Slider */}
//       <div className="relative h-[250px] sm:h-[350px] lg:h-[400px] rounded-lg overflow-hidden shadow-lg bg-gray-100">
//         {shuffledImages.map((src, index) => (
//           <img
//             key={src}
//             src={src}
//             alt={`Event ${index + 1}`}
//             className={`absolute inset-0 w-full h-full object-contain bg-gray-300 transition-opacity duration-1000 ${
//               index === currentImageIndex ? "opacity-100" : "opacity-0"
//             }`}
//           />
//         ))}
//       </div>

//       {/* Event Details */}
//       <div className="flex flex-col space-y-6">
//         <div className="overflow-auto p-2 max-h-48 sm:max-h-56 lg:max-h-64">
//           <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300">
//             {description}
//           </p>
//         </div>

//         {/* Non-Scrollable Icons Section */}
//         <div className="grid grid-cols-2 gap-4">
//           {iconTexts.map((item, index) => (
//             <IconText key={index} Icon={item.icon} text={item.text} />
//           ))}
//         </div>
//       </div>
//     </div>

//     {/* Event Highlights and Upcoming Events */}
//     <div className="grid gap-8 md:grid-cols-2">
//       <EventCard title="Event Highlights">
//         <ul className="list-disc list-inside space-y-2">
//           {highlights.map((highlight, index) => (
//             <li key={index}>{highlight}</li>
//           ))}
//         </ul>
//       </EventCard>

//       {/* Upcoming Events */}
//       <EventCard title="Upcoming Events">
//         <div className="space-y-4">
//           {upcomingEvents.map((event, index) => (
//             <div key={index}>
//               <h3 className="font-semibold text-gray-900 ">
//                 {event.title}
//               </h3>
//               <p className="text-sm text-gray-600 ">
//                 {event.description}
//               </p>
//               <p className="text-sm text-gray-600">Date: {event.date}</p>
//             </div>
//           ))}
//         </div>
//       </EventCard>
//     </div>

//     {/* Event Gallery */}
//     <section>
//       <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
//         Event Gallery
//       </h2>
//       <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
//         {shuffledImages.slice(0, 8).map((src, index) => (
//           <div
//             key={index}
//             className="relative aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-md transition-all duration-300 hover:scale-105"
//           >
//             <img
//               src={src}
//               alt={`Event ${index + 1}`}
//               className="w-full h-full object-cover"
//             />
//           </div>
//         ))}
//       </div>
//       <div className="mt-8 text-center">
//         <Link
//           to={galleryLink}
//           className="inline-flex items-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-300"
//         >
//           View Full Gallery
//         </Link>
//       </div>
//     </section>
//   </main>
// </div>

  );
};

export default AchievementReusableComponent;




